/**
 * Route Integration Hook
 * Phase 1: Cockpit Shell + Routing Unification
 * 
 * Bridges SurfaceRouter with WorkspaceContext for URL-based navigation.
 */

import { useEffect, useCallback } from 'react';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { getSurfaceRouter } from './SurfaceRouter';

/**
 * useRouteSync - Syncs workspace state with URL
 *
 * The URL is a side-effect of current state, not the source of truth.
 * On refresh, WorkspaceContext restores the selection from localStorage;
 * the URL is then updated to match (via the listeners below), not the
 * other way around. We only react to the URL on browser back/forward
 * (popstate) — NOT on mount, because doing so raced with localStorage
 * restoration and could silently override a "go to home" intent.
 */
export function useRouteSync(): {
  // F 320 — `kind` is optional and additive: every existing call site that
  // passes one argument keeps the same signature and the same behaviour.
  navigateToPage: (pageId: string, kind?: string) => void;
  getPageUrl: (pageId: string) => string;
} {
  const { currentPageId, selectPage, goToHome, initialized } = useWorkspace();
  const router = getSurfaceRouter();

  // Initialize router with valid surfaces
  useEffect(() => {
    router.initialize(['PageEditor', 'DatabaseView', 'HomePage', 'AIChat', 'QuickNote']);
  }, []);

  // Keep the URL in sync with currentPageId (one-way: state -> URL).
  // Gated on `initialized` so we don't clobber the URL on first render
  // before WorkspaceContext has loaded the saved page from localStorage.
  //
  // F 453 — BUT NOT OVER A DATABASE ROUTE. This effect is the reason a
  // bookmarked `/database/<id>` did not survive a page load. Measured, not
  // inferred: the path was correct for ~2.5s and then became `/` at ~5s, the
  // moment `initialized` flipped and `currentPageId` was still null, so the
  // else-branch wrote `/` over it.
  //
  // The bug is an ORDERING assumption: this hook treats the URL as purely a
  // function of workspace state, so a URL that addresses a surface the state
  // does not represent looks like stale state and gets "corrected". But a
  // `/database/:id` URL is a REQUEST to render a surface, and until something
  // consumes it the state is legitimately empty. "The URL disagrees with the
  // state" is exactly the condition under which the URL must win.
  //
  // So: a live database route is left alone here. The database surface reads
  // it on mount (WorkspaceSurface, F 453), and `popstate` still reconciles
  // back/forward. Page and home routes keep the original behaviour byte for
  // byte.
  useEffect(() => {
    if (!initialized) return;

    // Is the current URL addressing a database surface? Check the live
    // location, not router state: this effect runs precisely when the two have
    // drifted apart.
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const onDatabaseRoute = /^\/database\/[^/?#]+\/?$/.test(currentPath);
    if (onDatabaseRoute) return;

    // F 496 — A PAGE ROUTE IS A REQUEST, NOT STALE STATE.
    //
    // ⇒ ⇒⇒ MEASURED IN PRODUCTION, 2026-10-28, with the history instrumented:
    //   {"k":"push",    "u":"/page/3fffc18c-…", "at":"/database/db-quick-notes"}
    //   {"k":"replace", "u":"/",             "at":"/page/3fffc18c-…"}
    //   {"k":"popstate",                   "at":"/"}
    //   final url = /
    //
    // ⇒ ⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒ THE 'replace' LINE IS THIS EFFECT. The peek's
    // "Open in full page" wrote a '/page/<id>' URL. The workspace's
    // 'currentPageId' was STILL NULL — a database CARD is not a workspace
    // PAGE (the Card=Page model stores cards in the database's items, and
    // `selectPage` does `pages.find(p => p.id === pageId)`), so the state
    // could not confirm the URL. ⇒⇒⇒ This effect then read the URL as STALE
    // STATE and "corrected" it to '/'. ⇒⇒⇒ AND THE CORRECTION IS WHAT SENT
    // THE OPERATOR BACK TO "TODAY": the popstate handler saw '/' — no pageId,
    // no databaseId — and called goToHome().
    //
    // ⇒ ⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒ BUT A '/page/<id>' URL IS A REQUEST TO
    // RENDER A SURFACE. It is the SAME KIND of request as '/database/<id>' —
    // the F 453 comment above says exactly that, and the guard above already
    // honours it for databases. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ A URL THAT
    // NAMES A VALID PAGE ROUTE MUST NOT BE OVERWRITTEN BY A STATE THAT HAS
    // NOT CAUGHT UP YET. ⇒⇒⇒ THE URL WINS UNTIL THE STATE AGREES.
    const onPageRoute = /^\/page\/[^/?#]+\/?$/.test(currentPath);
    if (onPageRoute) return;

    if (currentPageId) {
      const url = router.buildUrl('PageEditor', { pageId: currentPageId });
      if (router.getCurrentUrl() !== url) {
        router.updateBrowserUrl(url, true);
      }
    } else {
      const url = '/';
      if (router.getCurrentUrl() !== url) {
        router.updateBrowserUrl(url, true);
      }
    }
  }, [currentPageId, initialized]);

  // React to browser back/forward only.
  useEffect(() => {
    const handlePopState = () => {
      const route = router.getCurrentRoute();
      if (route.params.pageId) {
        selectPage(route.params.pageId);
      } else if (route.params.databaseId) {
        // F 496 — A DATABASE ROUTE IS NOT "HOME".
        //
        // ⇒ ⇒⇒ MEASURED IN PRODUCTION, 2026-10-28: the peek's "Open in full
        // page" wrote '/page/<id>' with a pushState, then dispatched a
        // synthetic PopStateEvent. The popstate handler below ran, the URL was
        // not a page route it recognised in the workspace's terms, and the
        // else-branch called goToHome() — so the MAIN VIEW JUMPED TO "TODAY"
        // while the operator was still inside the database.
        //
        // ⇒ ⇒⇒ THE ROUTER REGISTERS '/database/:databaseId' WITH THE PARAM
        // NAME 'databaseId' (SurfaceRouter.tsx:102) — THERE IS NO 'pageId'
        // PARAM ON A DATABASE ROUTE. ⇒⇒⇒ So the old two-branch test
        // ("pageId? selectPage : goToHome") treated EVERY database route as
        // home, and the browser's own back button could send the operator from
        // a database to "Today" with no click of theirs.
        //
        // ⇒ ⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ THE ROUTE IS VALID, SO IT IS NOT HOME. A
        // popstate that names a surface the app can render must RENDER IT,
        // not discard it. ⇒⇒⇒ goToHome() IS RESERVED FOR A ROUTE THAT
        // NAMES NO SURFACE AT ALL.
        const dbId = route.params.databaseId;
        if (dbId) {
          // The database surface owns its own route consumption
          // (WorkspaceSurface's 'read' listener, F 453). Nothing to do here
          // beyond NOT throwing the operator back to the home surface.
          return;
        }
        goToHome();
      } else {
        goToHome();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectPage, goToHome]);

  // Navigate to a page and update URL (don't call selectPage - already called by caller)
  //
  // F 320 — a database row is a page of `type: 'database'`, and it has its own
  // surface. SurfaceRouter registers `/database/:databaseId` -> DatabaseView
  // (SurfaceRouter.tsx:102), and useRouteSync initializes DatabaseView as a valid
  // surface (line 31), so that route was registered and unreachable from any UI
  // path. The sidebar claimed a `ContentRouter` dispatched on the type; no such
  // component exists in the tree, so every selection landed on PageEditor.
  //
  // `kind` is optional. Absent or 'page' takes the original path byte for byte —
  // page routing is unchanged.
  const navigateToPage = useCallback((pageId: string, kind?: string) => {
    const url = kind === 'database'
      ? router.buildUrl('DatabaseView', { databaseId: pageId })
      : router.buildUrl('PageEditor', { pageId });
    router.updateBrowserUrl(url, true); // Replace current history entry
    // Note: selectPage is called by the caller (handlePageSelect)
  }, []);

  // Get URL for a page
  const getPageUrl = useCallback((pageId: string): string => {
    return router.buildUrl('PageEditor', { pageId });
  }, []);

  return { navigateToPage, getPageUrl };
}

export default useRouteSync;